export interface FoNavSebPastedRate {
    maturity: string;
    maturityYears: number | null;
    rate: number | null;
    change: number | null;
    time: string | null;
    date: string | null;
}

const HEADER_TOKENS = new Set(["maturity", "price", "+/-", "time", "date"]);

const MATURITY_PATTERN = /^(\d+(?:[.,]\d+)?)\s*(yr|yrs|y|year|years|mth|mths|m|mon|month|months)$/i;
const TIME_PATTERN = /^\d{1,2}:\d{2}(?::\d{2})?$/;
const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const SLASH_DATE_PATTERN = /^(\d{1,2})[/.](\d{1,2})[/.](\d{4})$/;
const NUMBER_PATTERN = /^[+-]?\d+(?:[.,]\d+)?$/;

/**
 * Copying out of a rendered web table brings along non-breaking spaces and typographic minus
 * signs, neither of which survive a plain numeric parse.
 */
function normalizePastedText(text: string): string {
    return text.replace(/\u00a0/g, " ").replace(/[\u2212\u2013\u2014]/g, "-");
}

export function parseSebMaturityYears(maturity: string): number | null {
    if (!maturity) {
        return null;
    }

    const match = maturity.trim().match(MATURITY_PATTERN);
    if (!match) {
        return null;
    }

    const value = Number(match[1].replace(",", "."));
    if (!Number.isFinite(value)) {
        return null;
    }

    if (match[2].toLowerCase().startsWith("y")) {
        return value;
    }
    return value / 12;
}

function padTwo(value: number): string {
    return value < 10 ? `0${value}` : String(value);
}

/**
 * SEB publishes dates day first, so 25/08/2026 is 25 August. Normalized to ISO here so the rest
 * of the page can treat it like every other date.
 */
function normalizePastedDate(token: string): string | null {
    if (ISO_DATE_PATTERN.test(token)) {
        return token;
    }

    const match = token.match(SLASH_DATE_PATTERN);
    if (!match) {
        return null;
    }

    const day = Number(match[1]);
    const month = Number(match[2]);
    if (day < 1 || day > 31 || month < 1 || month > 12) {
        return null;
    }

    return `${match[3]}-${padTwo(month)}-${padTwo(day)}`;
}

/**
 * Reads the swap rate table copied off SEB's rates page. Each tenor label starts a new row and the
 * values that follow are assigned by what they look like rather than by column position, so the
 * paste survives whether the browser produced one value per line or tab separated rows, and
 * whether or not the header row came along with it.
 */
export function parseSebSwapRatesPaste(text: string | null | undefined): FoNavSebPastedRate[] {
    if (!text || text.trim() === "") {
        return [];
    }

    const tokens = normalizePastedText(text)
        .split(/[\t\r\n]+/)
        .map((token) => token.trim())
        .filter((token) => token !== "" && !HEADER_TOKENS.has(token.toLowerCase()));

    const rows: FoNavSebPastedRate[] = [];
    let current: FoNavSebPastedRate | null = null;

    tokens.forEach((token) => {
        if (MATURITY_PATTERN.test(token)) {
            current = {
                maturity: token,
                maturityYears: parseSebMaturityYears(token),
                rate: null,
                change: null,
                time: null,
                date: null
            };
            rows.push(current);
            return;
        }

        if (current === null) {
            return;
        }

        if (TIME_PATTERN.test(token)) {
            current.time = token;
            return;
        }

        const date = normalizePastedDate(token);
        if (date !== null) {
            current.date = date;
            return;
        }

        if (NUMBER_PATTERN.test(token)) {
            const value = Number(token.replace(",", "."));
            if (!Number.isFinite(value)) {
                return;
            }
            if (current.rate === null) {
                current.rate = value;
            } else if (current.change === null) {
                current.change = value;
            }
        }
    });

    return rows;
}
